import { FormControlConfiguration } from '@zipari/shared-ds-util-form';
import { cloneObject } from '@zipari/web-utils';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  AddressMap,
  AddressMaps,
  altAddressFieldNames,
} from '@zipari/shared-sbp-directives';

// Generate autocomplete attributes for address fields (needed for browser autocomplete) and add them to the form control config
export function withAddressAutocomplete(
  inputControls: Array<FormControlConfiguration>,
  group: string,
  addressMaps?: AddressMaps,
): Array<FormControlConfiguration> {
  if (addressMaps) {
    let result;
    Object.keys(addressMaps).forEach((addressMapKey) => {
      const addressMap = addressMaps[addressMapKey];
      result = _withAddressAutocomplete(
        inputControls,
        addressMapKey,
        addressMap,
      );
    });
    return result;
  }

  return _withAddressAutocomplete(inputControls, group);
}

function _withAddressAutocomplete(
  inputControls: Array<FormControlConfiguration>,
  group: string,
  addressMap?: AddressMap,
): Array<FormControlConfiguration> {
  const clonedControls = cloneObject(inputControls);
  let propToAddressMap;

  // Map address fields to autocomplete attribute
  // https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill
  const autocompleteMap: AddressMap = {
    street_name_1: `section-${group} address-line1`,
    street_name_2: `section-${group} address-line2`,
    city: `section-${group} address-level2`,
    state: `section-${group} address-level1`,
    zipcode: `section-${group} postal-code`,
  };

  if (addressMap) {
    // Swap key and value on the address map
    propToAddressMap = Object.entries(addressMap).reduce(
      (acc, [key, value]) => ({ ...acc, [value]: key }),
      {},
    );
  } else {
    // When no address map is provided, identify fields based on prop name
    propToAddressMap = Object.entries(altAddressFieldNames).reduce(
      (
        acc,
        [standardAddressProp, altAddressProps]: [string, Array<string>],
      ) => {
        const possibleProps = [standardAddressProp, ...altAddressProps];
        inputControls.forEach((control) => {
          if (possibleProps.includes(control.prop)) {
            acc[control.prop] = standardAddressProp;
            return acc;
          }
        });
        return acc;
      },
      {},
    );
  }

  const controlConfigs = clonedControls.map((control) => {
    const standardAddressProp = propToAddressMap[control.prop];
    if (standardAddressProp) {
      return {
        ...control,
        autocomplete: autocompleteMap[standardAddressProp],
      };
    }
    return control;
  });

  return controlConfigs;
}
